import { useCallback, useEffect, useState } from 'react'
import { FaDesktop, FaMobileAlt, FaSignOutAlt, FaSyncAlt, FaTabletAlt } from 'react-icons/fa'
import api from '../../../helpers/apiRequester'
import { Banner, Button, Card, DataTable, EmptyState, Modal, StatusPill, useToast } from 'ui'
import { apiErrorMessage, DashboardSession, formatDateTime, formatRelative, signOut } from './shared'
import s from './styles.module.scss'

interface SessionsResponse {
    success: boolean
    available: boolean
    active: DashboardSession[]
    history: DashboardSession[]
    activeWindowDays: number
}

const DeviceIcon = ({ session }: { session: DashboardSession }) => {
    const device = (session.device || '').toLowerCase()
    if (device.includes('mobile') || device.includes('phone')) return <FaMobileAlt aria-hidden="true" />
    if (device.includes('tablet') || device.includes('ipad')) return <FaTabletAlt aria-hidden="true" />
    return <FaDesktop aria-hidden="true" />
}

/**
 * Where you're signed in (active sessions) + recent sign-in history, both
 * from `dashboard_sessions`, with "Sign out everywhere".
 */
export const SessionsCard = () => {
    const { toast } = useToast()
    const [data, setData] = useState<SessionsResponse | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [revoking, setRevoking] = useState(false)
    const [revokeError, setRevokeError] = useState<string | null>(null)

    const load = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const response = await api({ url: '/profile-sessions', data: {} })
            if (!response.data?.success) throw new Error(response.data?.error || 'Sessions unavailable')
            setData(response.data)
        } catch (err: any) {
            setError(apiErrorMessage(err, 'Could not load your sessions.'))
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        void load()
    }, [load])

    const signOutEverywhere = async () => {
        if (revoking) return
        setRevoking(true)
        setRevokeError(null)
        try {
            const response = await api({ url: '/profile-sign-out-everywhere', data: {} })
            if (!response.data?.success) throw new Error(response.data?.error || 'Sign out failed')
            toast({ title: 'Signed out everywhere', description: 'Signing you out of this device too…', tone: 'good' })
            // This session was revoked as well; finish the job locally.
            window.setTimeout(signOut, 900)
        } catch (err: any) {
            setRevokeError(apiErrorMessage(err, 'Could not sign out other sessions.'))
            setRevoking(false)
        }
    }

    const active = data?.active ?? []
    const history = data?.history ?? []
    const otherCount = active.filter((session) => !session.current).length

    return (
        <>
            <Card
                title="Where you're signed in"
                subtitle={
                    data?.available
                        ? `Devices that have used the dashboard in the last ${data.activeWindowDays} days and haven't been signed out.`
                        : 'Devices currently signed in to your account.'
                }
                actions={
                    <Button size="sm" variant="ghost" icon={<FaSyncAlt />} onClick={() => void load()} disabled={loading}>
                        Refresh
                    </Button>
                }
            >
                <div className={s.form}>
                    {error && (
                        <Banner tone="bad" title="Couldn't load sessions">
                            {error}
                        </Banner>
                    )}
                    {data && !data.available && (
                        <Banner tone="info" title="Session tracking isn't switched on yet">
                            Once the database migration has been applied, sign-ins will appear here automatically. You can still use
                            "Sign out everywhere" below.
                        </Banner>
                    )}

                    {loading && !data ? (
                        <EmptyState tone="loading" title="Loading sessions…" />
                    ) : active.length ? (
                        <ul className={s.sessionList}>
                            {active.map((session) => (
                                <li key={session.id} className={`${s.session} ${session.current ? s.sessionCurrent : ''}`}>
                                    <span className={s.sessionIcon}>
                                        <DeviceIcon session={session} />
                                    </span>
                                    <div className={s.sessionText}>
                                        <div className={s.sessionTitle}>
                                            <strong>{session.label}</strong>
                                            {session.current && (
                                                <StatusPill tone="good" size="sm" dot>
                                                    This device
                                                </StatusPill>
                                            )}
                                        </div>
                                        <span className={s.sessionMeta}>
                                            {session.ip ? `IP ${session.ip} · ` : ''}
                                            Signed in {formatDateTime(session.signedInAt)}
                                            {session.lastActiveAt ? ` · Last active ${formatRelative(session.lastActiveAt)}` : ''}
                                        </span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    ) : data?.available ? (
                        <EmptyState title="No active sessions recorded" description="Your next sign-in will appear here." />
                    ) : null}

                    <div className={s.formActions}>
                        <p className={s.formNote}>
                            {otherCount > 0
                                ? `${otherCount} other device${otherCount === 1 ? ' is' : 's are'} signed in.`
                                : 'Lost a laptop or shared a password? Sign out every device at once.'}
                        </p>
                        <Button variant="danger" icon={<FaSignOutAlt />} onClick={() => setConfirmOpen(true)}>
                            Sign out everywhere
                        </Button>
                    </div>
                </div>
            </Card>

            {data?.available && (
                <Card title="Recent sign-ins" subtitle="Your last 25 sign-ins to the dashboard, newest first." padding="none">
                    <DataTable<DashboardSession>
                        dense
                        bare
                        rows={history}
                        rowKey={(row) => row.id}
                        loading={loading && !data}
                        emptyTitle="No sign-ins recorded yet"
                        columns={[
                            {
                                key: 'when',
                                header: 'When',
                                nowrap: true,
                                render: (row) => formatDateTime(row.signedInAt),
                            },
                            { key: 'device', header: 'Device', render: (row) => row.label },
                            { key: 'ip', header: 'IP address', nowrap: true, render: (row) => row.ip || '—' },
                            {
                                key: 'status',
                                header: 'Status',
                                nowrap: true,
                                render: (row) =>
                                    row.current ? (
                                        <StatusPill tone="good" size="sm" dot>
                                            This session
                                        </StatusPill>
                                    ) : row.revokedAt ? (
                                        <StatusPill tone="neutral" size="sm">
                                            Signed out
                                        </StatusPill>
                                    ) : row.active ? (
                                        <StatusPill tone="info" size="sm">
                                            Active
                                        </StatusPill>
                                    ) : (
                                        <StatusPill tone="neutral" size="sm">
                                            Expired
                                        </StatusPill>
                                    ),
                            },
                        ]}
                    />
                </Card>
            )}

            <Modal
                open={confirmOpen}
                onClose={() => !revoking && setConfirmOpen(false)}
                title="Sign out of every device?"
                subtitle="Including this one — you'll be taken back to the login page."
                size="sm"
                busy={revoking}
                footer={
                    <>
                        <Button variant="ghost" onClick={() => setConfirmOpen(false)} disabled={revoking}>
                            Cancel
                        </Button>
                        <Button variant="danger" icon={<FaSignOutAlt />} onClick={() => void signOutEverywhere()} loading={revoking}>
                            Sign out everywhere
                        </Button>
                    </>
                }
            >
                {revokeError && (
                    <Banner tone="bad" title="Couldn't sign out">
                        {revokeError}
                    </Banner>
                )}
                <p className={s.modalText}>
                    Every browser and device signed in to your account will be logged out within a minute and will need your password
                    {' '}(and two-factor code, if enabled) to get back in. We'll email you to confirm.
                </p>
            </Modal>
        </>
    )
}
